import {
  CHAT_GET_CONTACTS,
  CHAT_GET_CONTACTS_SUCCESS,
  CHAT_GET_CONTACTS_FAILURE,
  CHAT_GET_CONVERSATIONS,
  CHAT_GET_CONVERSATIONS_SUCCESS,
  CHAT_GET_CONVERSATIONS_FAILURE,
  CHAT_CREATE_CONVERSATION_SUCCESS,
  CHAT_GET_MESSAGES,
  CHAT_GET_MESSAGES_SUCCESS,
  CHAT_GET_MESSAGES_FAILURE,
  CHAT_SEND_MESSAGE_SUCCESS,
  CHAT_RECEIVE_MESSAGE,
  CHAT_SET_ACTIVE_CONVERSATION,
  CHAT_TYPING_START,
  CHAT_TYPING_STOP,
  CHAT_USER_ONLINE,
  CHAT_USER_OFFLINE,
  CHAT_MESSAGE_READ,
  CHAT_REACTION_ADDED,
  CHAT_REACTION_REMOVED,
  CHAT_MESSAGE_EDITED,
  CHAT_MESSAGE_DELETED,
  CHAT_UPLOAD_FILE,
  CHAT_UPLOAD_FILE_SUCCESS,
  CHAT_UPLOAD_FILE_FAILURE,
  CHAT_LOAD_MORE_MESSAGES_SUCCESS,
} from "store/constants/Chat";

const INIT_STATE = {
  contacts: [],
  contactsLoading: false,
  conversations: [],
  conversationsLoading: false,
  activeConversation: null,
  messages: [],
  messagesLoading: false,
  messagesTotalCount: 0,
  typingUsers: {},
  onlineUsers: {},
  lastSeenTimes: {},
  uploadingFile: false,
};

const updateMessageInList = (messages, messageId, updater) => {
  return messages.map((msg) => (msg.messageId === messageId ? updater(msg) : msg));
};

const enrichParentMessage = (msg, messages) => {
  if (msg.parentMessageId && !msg.ParentMessage) {
    const parent = messages.find((m) => m.messageId === msg.parentMessageId);
    if (parent) {
      return {
        ...msg,
        ParentMessage: { messageId: parent.messageId, content: parent.content, isDeleted: parent.isDeleted },
      };
    }
  }
  return msg;
};

export default (state = INIT_STATE, action) => {
  switch (action.type) {
    case CHAT_GET_CONTACTS:
      return { ...state, contactsLoading: true };
    case CHAT_GET_CONTACTS_SUCCESS: {
      const contactOnlineUsers = {};
      const contactLastSeen = {};
      (action.payload || []).forEach((c) => {
        const key = `${c.userId}_${c.userType}`;
        if (c.isOnline) {
          contactOnlineUsers[key] = true;
        }
        if (c.lastSeen) {
          contactLastSeen[key] = c.lastSeen;
        }
      });
      return {
        ...state,
        contactsLoading: false,
        contacts: action.payload,
        onlineUsers: { ...state.onlineUsers, ...contactOnlineUsers },
        lastSeenTimes: { ...state.lastSeenTimes, ...contactLastSeen },
      };
    }
    case CHAT_GET_CONTACTS_FAILURE:
      return { ...state, contactsLoading: false };

    case CHAT_GET_CONVERSATIONS:
      return { ...state, conversationsLoading: true };
    case CHAT_GET_CONVERSATIONS_SUCCESS: {
      const convOnlineUsers = {};
      const convLastSeen = {};
      (action.payload || []).forEach((conv) => {
        (conv.Participants || []).forEach((p) => {
          const key = `${p.userId}_${p.userType}`;
          if (p.isOnline) {
            convOnlineUsers[key] = true;
          }
          if (p.lastSeen) {
            convLastSeen[key] = p.lastSeen;
          }
        });
      });
      return {
        ...state,
        conversationsLoading: false,
        conversations: action.payload,
        onlineUsers: { ...state.onlineUsers, ...convOnlineUsers },
        lastSeenTimes: { ...state.lastSeenTimes, ...convLastSeen },
      };
    }
    case CHAT_GET_CONVERSATIONS_FAILURE:
      return { ...state, conversationsLoading: false };

    case CHAT_CREATE_CONVERSATION_SUCCESS: {
      const exists = state.conversations.find((c) => c.conversationId === action.payload.conversationId);
      return {
        ...state,
        activeConversation: action.payload,
        conversations: exists ? state.conversations : [action.payload, ...state.conversations],
      };
    }

    case CHAT_SET_ACTIVE_CONVERSATION:
      return { ...state, activeConversation: action.payload, messages: [], messagesTotalCount: 0 };

    case CHAT_GET_MESSAGES:
      return { ...state, messagesLoading: true };
    case CHAT_GET_MESSAGES_SUCCESS:
      return {
        ...state,
        messagesLoading: false,
        messages: action.payload.rows.reverse(),
        messagesTotalCount: action.payload.count,
      };
    case CHAT_GET_MESSAGES_FAILURE:
      return { ...state, messagesLoading: false };

    case CHAT_LOAD_MORE_MESSAGES_SUCCESS:
      return {
        ...state,
        messages: [...action.payload.rows.reverse(), ...state.messages],
        messagesTotalCount: action.payload.count,
      };

    case CHAT_SEND_MESSAGE_SUCCESS: {
      const sentMsg = enrichParentMessage(action.payload, state.messages);
      const sentAlreadyExists = state.messages.find((m) => m.messageId === sentMsg.messageId);
      if (sentAlreadyExists) return state;

      const sentIsActiveConv = state.activeConversation?.conversationId === sentMsg.conversationId;
      const sentUpdatedConversations = state.conversations.map((conv) => {
        if (conv.conversationId === sentMsg.conversationId) {
          return { ...conv, lastMessage: sentMsg, updatedAt: sentMsg.createdAt };
        }
        return conv;
      });

      return {
        ...state,
        messages: sentIsActiveConv ? [...state.messages, sentMsg] : state.messages,
        conversations: sentUpdatedConversations.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
      };
    }

    case CHAT_RECEIVE_MESSAGE: {
      const msg = enrichParentMessage(action.payload, state.messages);
      const alreadyExists = state.messages.find((m) => m.messageId === msg.messageId);
      if (alreadyExists) return state;

      const isActiveConv = state.activeConversation?.conversationId === msg.conversationId;
      const isFromOther = msg.senderId !== msg.currentUserId;

      const updatedConversations = state.conversations.map((conv) => {
        if (conv.conversationId === msg.conversationId) {
          return {
            ...conv,
            lastMessage: msg,
            updatedAt: msg.createdAt,
            unreadCount: isFromOther && !isActiveConv ? (conv.unreadCount || 0) + 1 : conv.unreadCount,
          };
        }
        return conv;
      });

      return {
        ...state,
        messages: isActiveConv ? [...state.messages, msg] : state.messages,
        conversations: updatedConversations.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
      };
    }

    case CHAT_TYPING_START: {
      const { userId, conversationId } = action.payload;
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [conversationId]: {
            ...(state.typingUsers[conversationId] || {}),
            [userId]: true,
          },
        },
      };
    }
    case CHAT_TYPING_STOP: {
      const { userId, conversationId } = action.payload;
      const convTyping = { ...(state.typingUsers[conversationId] || {}) };
      delete convTyping[userId];
      return {
        ...state,
        typingUsers: { ...state.typingUsers, [conversationId]: convTyping },
      };
    }

    case CHAT_USER_ONLINE:
      return {
        ...state,
        onlineUsers: { ...state.onlineUsers, [`${action.payload.userId}_${action.payload.userType}`]: true },
      };
    case CHAT_USER_OFFLINE: {
      const key = `${action.payload.userId}_${action.payload.userType}`;
      const newOnline = { ...state.onlineUsers };
      delete newOnline[key];
      return {
        ...state,
        onlineUsers: newOnline,
        lastSeenTimes: {
          ...state.lastSeenTimes,
          [key]: action.payload.lastSeen || new Date().toISOString(),
        },
      };
    }

    case CHAT_MESSAGE_READ: {
      const { conversationId, userId, userType } = action.payload;
      const updatedConvs = state.conversations.map((conv) => {
        if (conv.conversationId === conversationId) {
          return { ...conv, unreadCount: 0 };
        }
        return conv;
      });
      const { currentUserId } = action.payload;
      let updatedMessages = state.messages;
      if (userId && userId !== currentUserId && state.activeConversation?.conversationId === conversationId) {
        updatedMessages = state.messages.map((msg) => {
          if (msg.senderId === currentUserId) {
            const alreadyRead = (msg.ReadReceipts || []).some((r) => r.userId === userId && r.userType === userType);
            if (!alreadyRead) {
              return {
                ...msg,
                ReadReceipts: [...(msg.ReadReceipts || []), { userId, userType, readAt: new Date().toISOString() }],
              };
            }
          }
          return msg;
        });
      }
      return {
        ...state,
        conversations: updatedConvs,
        messages: updatedMessages,
      };
    }

    case CHAT_REACTION_ADDED: {
      const { messageId, reaction } = action.payload;
      return {
        ...state,
        messages: updateMessageInList(state.messages, messageId, (msg) => {
          const alreadyExists = (msg.Reactions || []).some(
            (r) => r.userId === reaction.userId && r.userType === reaction.userType && r.emoji === reaction.emoji
          );
          if (alreadyExists) return msg;
          return {
            ...msg,
            Reactions: [...(msg.Reactions || []), reaction],
          };
        }),
      };
    }
    case CHAT_REACTION_REMOVED: {
      const { messageId, userId, emoji } = action.payload;
      return {
        ...state,
        messages: updateMessageInList(state.messages, messageId, (msg) => ({
          ...msg,
          Reactions: (msg.Reactions || []).filter((r) => !(r.userId === userId && r.emoji === emoji)),
        })),
      };
    }

    case CHAT_MESSAGE_EDITED: {
      const editedMsg = action.payload;
      return {
        ...state,
        messages: updateMessageInList(state.messages, editedMsg.messageId, () => editedMsg),
      };
    }
    case CHAT_MESSAGE_DELETED: {
      const { messageId } = action.payload;
      return {
        ...state,
        messages: updateMessageInList(state.messages, messageId, (msg) => ({
          ...msg,
          isDeleted: true,
          content: null,
        })),
      };
    }

    case CHAT_UPLOAD_FILE:
      return { ...state, uploadingFile: true };
    case CHAT_UPLOAD_FILE_SUCCESS: {
      const fileMsg = enrichParentMessage(action.payload, state.messages);
      const fileAlreadyExists = state.messages.find((m) => m.messageId === fileMsg.messageId);
      if (fileAlreadyExists) return { ...state, uploadingFile: false };

      const fileIsActiveConv = state.activeConversation?.conversationId === fileMsg.conversationId;
      const fileUpdatedConversations = state.conversations.map((conv) => {
        if (conv.conversationId === fileMsg.conversationId) {
          return { ...conv, lastMessage: fileMsg, updatedAt: fileMsg.createdAt };
        }
        return conv;
      });

      return {
        ...state,
        uploadingFile: false,
        messages: fileIsActiveConv ? [...state.messages, fileMsg] : state.messages,
        conversations: fileUpdatedConversations.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
      };
    }
    case CHAT_UPLOAD_FILE_FAILURE:
      return { ...state, uploadingFile: false };

    default:
      return state;
  }
};
